"use client";
import React, { useState, useEffect } from "react";

interface LogoAnimatedGreeksProps {
  className?: string;
  interval?: number;
  filterId?: string;
}

const greekLetters = [
  {
    name: "Delta",
    path: "M 50 25 L 70 70 L 30 70 Z",
    type: "path"
  },
  {
    name: "Theta",
    circle: { cx: 50, cy: 50, r: 12 },
    line: { x1: 40, y1: 50, x2: 60, y2: 50 },
    type: "theta"
  },
  {
    name: "Gamma",
    path: "M 35 25 L 65 25 L 65 32 L 45 32 L 45 75",
    type: "path"
  },
  {
    name: "Vega",
    path: "M 35 25 L 50 70 L 65 25",
    type: "path"
  },
  {
    name: "Rho",
    path: "M 35 25 L 35 75 M 35 25 C 35 25 65 25 65 40 C 65 55 35 55 35 55",
    type: "path"
  }
];

export default function LogoAnimatedGreeks({
  className = "w-16 h-16",
  interval = 3000,
  filterId = "greeks",
}: LogoAnimatedGreeksProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  useEffect(() => {
    const timer = setInterval(() => {
      setIsTransitioning(true);
      setTimeout(() => {
        setCurrentIndex((prev) => (prev + 1) % greekLetters.length);
        setIsTransitioning(false);
      }, 500);
    }, interval);

    return () => clearInterval(timer);
  }, [interval]);

  const currentLetter = greekLetters[currentIndex];

  return (
    <svg
      viewBox="0 0 100 100"
      className={className}
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        <filter id={`glow-animated-${filterId}`} suppressHydrationWarning>
          <feGaussianBlur stdDeviation="2" result="coloredBlur" />
          <feMerge>
            <feMergeNode in="coloredBlur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>

      <path
        d="M 10 50 L 25 50 L 32 35 L 40 65 L 48 40 L 56 55 L 68 50 L 90 50"
        stroke="#FF6B00"
        strokeWidth="2.5"
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
        filter={`url(#glow-animated-${filterId})`}
        suppressHydrationWarning
      />

      <g
        style={{
          opacity: isTransitioning ? 0 : 1,
          transition: "opacity 0.5s ease-in-out"
        }}
      >
        {currentLetter.type === "theta" && currentLetter.circle && currentLetter.line && (
          <>
            <circle
              cx={currentLetter.circle.cx}
              cy={currentLetter.circle.cy}
              r={currentLetter.circle.r}
              stroke="white"
              strokeWidth="3"
              fill="none"
            />
            <line
              x1={currentLetter.line.x1}
              y1={currentLetter.line.y1}
              x2={currentLetter.line.x2}
              y2={currentLetter.line.y2}
              stroke="white"
              strokeWidth="3"
              strokeLinecap="round"
            />
          </>
        )}
        {currentLetter.type === "path" && currentLetter.path && (
          <path
            d={currentLetter.path}
            stroke="white"
            strokeWidth="4"
            strokeLinecap="round"
            strokeLinejoin="round"
            fill="none"
          />
        )}
      </g>

      <text
        x="50"
        y="92"
        textAnchor="middle"
        fill="white"
        fontSize="8"
        opacity="0.5"
        fontFamily="monospace"
      >
        {currentLetter.name}
      </text>
    </svg>
  );
}
